import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, TableCell, TableRow, Typography } from '@mui/material'
import { LoadingIndicator } from '@/apps/latest/components/atoms/LoadingIndicator'
import SearchBox from '@/apps/latest/components/portal/SearchBox'
import PaginationControl from '@/apps/latest/components/portal/PaginationControl'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
  Banner,
  ConfirmDialog,
  DataTable,
  NotionButton,
  NotionCheckbox,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  useDrugGroupDetailQuery,
  useDrugGroupDrugs,
  useDrugSearchQuery,
} from '@/features/internal/drugs'
import { useToast } from '@/shared/contexts/ToastContext'
import { DRUG_SELECTION } from '@/shared/constants/const'

/**
 * 医薬品グループ用医薬品検索・追加画面。
 * 検索は部分一致の通常検索のみ (高度検索は ADR-0017 で撤去)。
 */
const DrugGroupDrugsPage: React.FC = () => {
  // URLからグループIDを取得
  const { id: groupId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // URLパラメータから検索条件を取得 (旧・高度検索形式の値は復元対象外)
  const searchParams = new URLSearchParams(location.search)
  const rawSearchParam = searchParams.get('search') || ''
  const initialKeyword = rawSearchParam.startsWith('[高度]')
    ? ''
    : rawSearchParam

  // 医薬品グループ詳細クエリ（TanStack Query）
  const {
    data: selectedGroup,
    isLoading: groupLoading,
    error: groupError,
  } = useDrugGroupDetailQuery(groupId)

  const {
    loading: groupDrugsLoading,
    error: groupDrugsError,
    groupDrugs,
    selectedDrugIds,
    addDrugsToGroupAction,
    toggleDrugSelection,
    selectAllDrugs,
    selectAllSearchResults,
    clearSelection,
  } = useDrugGroupDrugs(groupId)

  const { showToast } = useToast()

  // 共通状態
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  // 医薬品検索フック（TanStack Query）
  const {
    normalSearchKeyword,
    setNormalSearchKeyword,
    searchResults,
    isLoading: searchLoading,
    error: searchError,
    executeNormalSearch,
    currentPage: searchCurrentPage,
  } = useDrugSearchQuery({
    initialSearchKeyword: initialKeyword,
  })

  // ページ初期化フラグ（初回ロードとリロード時のみURL復元を行う）
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)

  // 検索状態を再初期化（URLパラメータから）- 初回ロード時のみ
  useEffect(() => {
    if (!isInitialLoad) {
      return
    }

    const params = new URLSearchParams(location.search)
    const newSearchParam = params.get('search') || ''
    const newPageParam = params.get('page')
      ? parseInt(params.get('page') || '1', 10)
      : 1

    if (newSearchParam && !newSearchParam.startsWith('[高度]')) {
      executeNormalSearch(newSearchParam, newPageParam)
    }
    setIsInitialLoad(false)
  }, [executeNormalSearch, isInitialLoad, location.search])

  // グループ内の医薬品はuseDrugGroupDrugs(groupId)でgroupId変更時に自動取得

  // 通常検索ハンドラー
  const handleNormalSearch = useCallback(() => {
    if (!normalSearchKeyword) return

    // 検索条件変更時に選択状態をクリア
    clearSelection()
    setSelectAllChecked(false)

    executeNormalSearch(normalSearchKeyword, 1)

    // URLパラメータを更新
    const params = new URLSearchParams()
    params.set('search', normalSearchKeyword)
    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    )
  }, [
    normalSearchKeyword,
    clearSelection,
    executeNormalSearch,
    navigate,
    location.pathname,
  ])

  // ページネーション処理
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (normalSearchKeyword) {
        executeNormalSearch(normalSearchKeyword, newPage)
      }
    },
    [normalSearchKeyword, executeNormalSearch]
  )

  // 医薬品選択処理
  const handleDrugSelect = (drugId: string) => {
    toggleDrugSelection(drugId)
  }

  // 全選択/解除処理
  const handleSelectAll = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked
    setSelectAllChecked(checked)

    if (checked) {
      // 「全て選択」実行前に既存の選択をクリア（混在を防ぐ）
      clearSelection()

      if (displayData.hasSearch) {
        // 検索結果の全件選択
        try {
          const result = await selectAllSearchResults(normalSearchKeyword)
          if (result.totalFound > DRUG_SELECTION.MAX_SELECTION_COUNT) {
            setSelectAllChecked(false)
          }
          showSelectAllMessage(result)
        } catch (_error) {
          showToast('全件選択に失敗しました', 'error')
          setSelectAllChecked(false)
        }
      } else {
        // 検索していない場合は、現在表示中の薬剤を選択
        selectAllDrugs(displayData.drugs)
      }
    } else {
      // 選択解除の場合は常に全選択をクリア
      clearSelection()
    }
  }

  // 全件選択結果メッセージの表示
  const showSelectAllMessage = (result: {
    totalFound: number
    selectedCount: number
    alreadyInGroup: number
  }) => {
    if (result.totalFound > DRUG_SELECTION.MAX_SELECTION_COUNT) {
      showToast(
        `${DRUG_SELECTION.MAX_SELECTION_COUNT}件以上の医薬品は一括選択できません`,
        'warning'
      )
      return
    }

    showToast(`${result.selectedCount}件の医薬品を選択しました`, 'success')
  }

  // 追加して保存 (選択0件時はボタン disabled)
  const handleSave = async () => {
    try {
      const success = await addDrugsToGroupAction(groupId)

      if (success) {
        setSaveSuccess(true)

        // 成功メッセージを表示した後に詳細画面へ戻る
        setTimeout(() => {
          navigate(`/portal/soaps/groups/${groupId}`)
        }, 2000)
      }
    } catch (_err) {
      // Handle error silently
    }
  }

  // キャンセル処理 (= 選択ありなら ConfirmDialog で確認)
  const handleCancel = () => {
    if (selectedDrugIds.length > 0) {
      setCancelConfirmOpen(true)
      return
    }
    navigate(`/portal/soaps/groups/${groupId}`)
  }

  const handleCancelConfirm = () => {
    setCancelConfirmOpen(false)
    navigate(`/portal/soaps/groups/${groupId}`)
  }

  // 表示データ
  const displayData = {
    drugs: searchResults?.results || [],
    loading: searchLoading,
    error: searchError?.message ?? null,
    currentPage: searchCurrentPage,
    totalPages: searchResults
      ? Math.ceil(searchResults.count / (searchResults.page_size || 20))
      : 1,
    hasSearch: !!normalSearchKeyword,
  }

  // ローディング状態
  const isLoading = displayData.loading || groupLoading || groupDrugsLoading

  // エラーがあるかどうか
  const hasError = !!displayData.error || !!groupError || !!groupDrugsError

  // 「全て選択」チェックボックスの状態を管理
  const selectAllCheckedState = useMemo(() => {
    if (displayData.drugs.length === 0) {
      return false
    }

    // 現在表示中の薬剤のうち、選択可能な薬剤（グループに未追加）を取得
    const selectableDrugs = displayData.drugs.filter(
      (drug) => !groupDrugs?.some((groupDrug) => groupDrug.id === drug.id)
    )

    // 選択可能な薬剤がすべて選択されているかチェック
    return (
      selectableDrugs.length > 0 &&
      selectableDrugs.every((drug) => selectedDrugIds.includes(drug.id))
    )
  }, [displayData.drugs, selectedDrugIds, groupDrugs])

  // useMemoの結果を状態に反映（派生状態の管理）
  useEffect(() => {
    setSelectAllChecked(selectAllCheckedState)
  }, [selectAllCheckedState])

  return (
    <PageScaffold title="医薬品を追加">
      <Box>
        <Typography sx={{ ...PORTAL_TEXT.caption, mb: 2 }}>
          「{selectedGroup?.name || '医薬品グループ'}」に追加します
        </Typography>

        {saveSuccess && (
          <Box sx={{ mb: 3 }}>
            <Banner severity="success">
              選択した医薬品がグループに追加されました。
            </Banner>
          </Box>
        )}
        {hasError && (
          <Box sx={{ mb: 3 }}>
            <Banner severity="danger">
              操作中にエラーが発生しました。しばらくしてから再度お試しください。
            </Banner>
          </Box>
        )}

        {/* 検索部分 */}
        <Box sx={{ mb: 3 }}>
          <SearchBox
            value={normalSearchKeyword}
            onChange={setNormalSearchKeyword}
            onSearch={handleNormalSearch}
            placeholder="医薬品名またはYJコードで検索"
          />
        </Box>

        {/* 全て選択 */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <NotionCheckbox
            checked={selectAllChecked}
            onChange={handleSelectAll}
            disabled={displayData.drugs.length === 0 || !displayData.hasSearch}
            label="検索結果を全て選択"
          />
        </Box>

        {/* ローディング表示 */}
        {isLoading && (
          <Box my={4}>
            <LoadingIndicator />
          </Box>
        )}

        {!isLoading && (
          <>
            {/* 医薬品テーブル */}
            <Box sx={{ mb: 3 }}>
              <DataTable
                columns={[
                  { key: 'select', label: '' },
                  { key: 'yj_code', label: 'YJコード' },
                  { key: 'product_name', label: '医薬品名' },
                  { key: 'manufacturer', label: '製造元' },
                ]}
              >
                {displayData.drugs.length > 0 ? (
                  displayData.drugs.map((drug) => {
                    const isAlreadyInGroup = groupDrugs?.some(
                      (groupDrug) => groupDrug.id === drug.id
                    )

                    return (
                      <TableRow key={drug.id}>
                        <TableCell padding="checkbox">
                          <NotionCheckbox
                            checked={selectedDrugIds.includes(drug.id)}
                            onChange={() => handleDrugSelect(drug.id)}
                            disabled={isAlreadyInGroup}
                          />
                        </TableCell>
                        <TableCell>{drug.yj_code}</TableCell>
                        <TableCell>
                          {drug.product_name ? (
                            drug.product_name
                          ) : (
                            <Box
                              component="span"
                              sx={{ color: 'text.secondary' }}
                            >
                              【般】{drug.general_name}
                            </Box>
                          )}
                          {isAlreadyInGroup && (
                            <Typography
                              component="span"
                              sx={{ ...PORTAL_TEXT.cellMeta, ml: 1 }}
                            >
                              追加済み
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{drug.manufacturer}</TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography sx={PORTAL_TEXT.emptyHint}>
                        {displayData.hasSearch
                          ? '(該当なし)'
                          : '医薬品名で検索'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </DataTable>
            </Box>

            {/* ページネーション */}
            <Box sx={{ mb: 3 }}>
              <PaginationControl
                currentPage={displayData.currentPage}
                totalPages={displayData.totalPages}
                onPageChange={handlePageChange}
              />
            </Box>

            {/* アクションボタン */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 4,
              }}
            >
              <NotionButton variant="secondary" onClick={handleCancel}>
                キャンセル
              </NotionButton>
              <NotionButton
                variant="primary"
                onClick={handleSave}
                disabled={isLoading || selectedDrugIds.length === 0}
              >
                {selectedDrugIds.length}件追加して保存
              </NotionButton>
            </Box>
          </>
        )}
      </Box>

      <ConfirmDialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={handleCancelConfirm}
        title="選択内容の破棄"
        message="選択した薬剤が破棄されますが、よろしいですか？"
        confirmLabel="破棄する"
        variant="danger"
      />
    </PageScaffold>
  )
}

export default DrugGroupDrugsPage
